import { Outlet, useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  IntegrationsJoinOrderStatusesEnumValues,
  IntegrationsJoinPremiumOfferTypesEnum,
  IntegrationsJoinPremiumOfferTypesEnumValues,
} from 'generated/resources/datatable.graphql'
import {
  IntegrationsJoinOrderStatusesEnum,
  IntegrationsJoinPurchaseRequestsCollectionDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { getOrderStatusColor, getPremiumOfferTypeLabel } from 'modules/ats/lib/utils/premiumPortals'
import { usePricingUtils } from 'modules/ats/pages/JobPostingMarketplace/usePricingUtils'

interface Props {
  canNavigateToJobs: boolean
}

interface atsJoinOrdersTableFilter {
  statuses: IntegrationsJoinOrderStatusesEnum[] | undefined
  premiumOfferTypes: IntegrationsJoinPremiumOfferTypesEnum[] | undefined
}

export const PurchaseRequestDataCollection = ({ canNavigateToJobs }: Props) => {
  const { formatPrice } = usePricingUtils()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [savedFilters, setSavedFilters] = useLocalStorage<atsJoinOrdersTableFilter | undefined>(
    LOCAL_STORAGE_KEYS.atsJoinOrdersTableFilter
  )

  const orderStatusLabels: Record<IntegrationsJoinOrderStatusesEnum, string> = {
    pending: i18n.t('ats.purchase_requests_page.table.filtering.statuses.pending'),
    processing: i18n.t('ats.purchase_requests_page.table.filtering.statuses.processing'),
    approved: i18n.t('ats.purchase_requests_page.table.filtering.statuses.approved'),
    cancelled: i18n.t('ats.purchase_requests_page.table.filtering.statuses.cancelled'),
    rejected: i18n.t('ats.purchase_requests_page.table.filtering.statuses.rejected'),
    failed: i18n.t('ats.purchase_requests_page.table.filtering.statuses.failed'),
    payment_failed: i18n.t('ats.purchase_requests_page.table.filtering.statuses.payment_failed'),
  }

  const source = useGraphqlDataCollectionSource({
    document: IntegrationsJoinPurchaseRequestsCollectionDocument,
    mapData: (data) => data.integrationsJoin.ordersConnection.nodes,
    mapPagination: (data) => ({
      total: data.integrationsJoin.ordersConnection.totalCount ?? 0,
    }),

    mapVariables: ({ sortings, filters }) => {
      const statuses = filters.status && filters.status.length > 0 ? filters.status : undefined
      const premiumOfferTypes =
        filters.premiumOfferType && filters.premiumOfferType.length > 0
          ? filters.premiumOfferType
          : undefined

      setSavedFilters({ statuses, premiumOfferTypes })

      return {
        statuses,
        premiumOfferTypes,
        sort: sortings?.[0],
      }
    },
    defaultSorting: {
      field: 'createdAt',
      order: 'desc',
    },
    currentFilters: { status: savedFilters?.statuses ?? undefined },
    filters: {
      premiumOfferType: {
        label: i18n.t('ats.purchase_requests_page.common.offer_type'),
        type: 'in',
        options: {
          options: IntegrationsJoinPremiumOfferTypesEnumValues.map((type) => ({
            label: getPremiumOfferTypeLabel(type),
            value: type,
          })),
        },
      },
      status: {
        label: i18n.t('ats.purchase_requests_page.common.status'),
        type: 'in',
        options: {
          options: IntegrationsJoinOrderStatusesEnumValues.map((status) => ({
            label: orderStatusLabels[status],
            value: status,
          })),
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.purchase_requests_page.table.filtering.statuses.pending'),
        filter: { status: ['pending', 'processing'] },
      },
      {
        label: i18n.t('ats.purchase_requests_page.table.filtering.statuses.approved'),
        filter: { status: ['approved'] },
      },
      {
        label: i18n.t('ats.purchase_requests_page.table.filtering.statuses.cancelled'),
        filter: { status: ['cancelled'] },
      },
    ],
    sortings: {
      // TODO: see if can pull this from the ats job posting as is not a direct relationship
      // atsJobPostingTitle: {
      //   label: 'Job posting title',
      // },
      createdAt: {
        label: i18n.t('ats.purchase_requests_page.common.requested_at'),
        sortBy: 'createdAt',
      },
      status: {
        label: 'Status',
        sortBy: 'status',
      },
      premiumOfferName: {
        label: i18n.t('ats.purchase_requests_page.table.sortings.offer_name'),
        sortBy: 'premiumOfferName',
      },
      premiumOfferPrice: {
        label: i18n.t('ats.purchase_requests_page.common.price'),
        sortBy: 'premiumOfferPrice',
      },
      premiumOfferType: {
        label: i18n.t('ats.purchase_requests_page.common.offer_type'),
        sortBy: 'premiumOfferType',
      },
    },
    grouping: {
      collapsible: true,
      groupBy: {
        'atsJobPosting.title': {
          name: i18n.t('ats.purchase_requests_page.common.job_posting'),
          label(groupId) {
            return String(groupId)
          },
        },
        premiumOfferTypeLabel: {
          name: i18n.t('ats.purchase_requests_page.common.offer_type'),
          label(groupId) {
            return String(groupId)
          },
        },
        statusLabel: {
          name: i18n.t('ats.purchase_requests_page.common.status'),
          label(groupId) {
            return String(groupId)
          },
        },
      },
    },
    itemActions(item) {
      return canNavigateToJobs
        ? [
            {
              label: i18n.t('ats.purchase_requests_page.table.item_actions.link_to_job'),
              onClick: () =>
                navigate(
                  navigation.ats.jobs.jobsId.fullPath({
                    jobId: item.atsJobPosting.id,
                  })
                ),
            },
          ]
        : []
    },
    itemOnClick: (item) => () => {
      navigate(
        navigation.ats.purchaseRequests.detail.fullPath({
          orderId: item.id,
        })
      )
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns: [
                {
                  label: i18n.t('ats.purchase_requests_page.table.headers.job_posting'),
                  render: ({ atsJobPosting }) => atsJobPosting.title,
                },
                {
                  label: i18n.t('ats.purchase_requests_page.table.headers.product_name'),
                  render: ({ premiumOfferName }) => premiumOfferName,
                  sorting: 'premiumOfferName',
                },
                {
                  label: i18n.t('ats.purchase_requests_page.table.headers.portals'),
                  render: ({ snapshot }) => {
                    const jobBoards = snapshot.products.map((product) => product.jobBoard)

                    return {
                      type: 'avatarList',
                      value: {
                        max: 4,
                        type: 'company',
                        avatarList: jobBoards.map((jobBoard) => ({
                          name: jobBoard.name,
                          src: jobBoard.logoUrl,
                        })),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('ats.purchase_requests_page.table.headers.type'),
                  render: ({ premiumOfferTypeLabel }) => premiumOfferTypeLabel,
                  sorting: 'premiumOfferType',
                },
                {
                  label: i18n.t('ats.purchase_requests_page.table.headers.requested_by'),
                  render: ({ requestedBy }) => ({
                    type: 'person',
                    value: {
                      firstName: requestedBy.firstName,
                      lastName: requestedBy.lastName,
                      src: buildUrl(requestedBy.avatar),
                    },
                  }),
                },
                {
                  label: i18n.t('ats.purchase_requests_page.table.headers.requested_at'),
                  sorting: 'createdAt',
                  render: ({ createdAt }) => ({
                    type: 'date',
                    value: dateBackendDateTimeToUI(createdAt),
                  }),
                },
                {
                  label: i18n.t('ats.purchase_requests_page.table.headers.price'),
                  render: ({ snapshot }) => formatPrice(snapshot.price, snapshot.currency),
                  sorting: 'premiumOfferPrice',
                },
                {
                  label: i18n.t('ats.purchase_requests_page.table.headers.status'),
                  sorting: 'status',
                  render: ({ status, statusLabel }) => ({
                    type: 'status',
                    value: {
                      status: getOrderStatusColor(status),
                      label: statusLabel,
                    },
                  }),
                },
              ],
            },
          },
        ]}
      />

      <Outlet />
    </>
  )
}
